/**
 * Connector management contracts — used by the connectors handler.
 *
 * Covers CRUD over `leviathan_master.connectors`, connectivity testing against
 * external sources (MySQL, PostgreSQL, WooCommerce, PrestaShop, Shopify,
 * generic REST), 30/7-day run statistics, and the multi-step CSV/XLSX import
 * flow (`upload → confirm → poll status`).
 *
 * Response contracts are entirely camelCase. Connector `config` mirrors the JSON
 * column as an untyped record (type-specific validation lives at the handler
 * edge); sensitive fields (`password`, `api_key`, `secret`, `consumer_secret`)
 * are masked by the handler before serialization.
 */
import { z } from 'zod';
import { ConnectorId, ISO8601Date, JobRunId, NonNegativeInt, TenantId, TenantSlug, UploadId, UserId, } from '../primitives/index.js';
import { ConnectorStatus, ConnectorType, JobRunStatus, UploadStatus, } from '../enums/index.js';
// ============================================================================
// Shared building blocks
// ============================================================================
/**
 * Connector-type-agnostic config envelope. Handler-level validators enforce
 * required fields per `ConnectorType` (see `createConnector` operation).
 */
export const ConnectorConfigBlobSchema = z.record(z.string(), z.unknown());
// Type-specific config/credentials schemas. Re-exported from provider files.
export { WooCommerceConfigSchema, WooCommerceCredentialsSchema, WooCommerceCursorSchema, } from './woocommerce.js';
/**
 * Credentials payload (write-only). Stored in AWS Secrets Manager at
 * `leviathan/{tenantId}/connector/{connectorId}` and NEVER persisted in DB.
 */
export const ConnectorCredentialsSchema = z.record(z.string(), z.string());
/** Statuses writable via `POST /connectors/:id` and `PUT /connectors/:id`. */
export const ConnectorWritableStatus = z.enum(['active', 'paused', 'testing']);
// ============================================================================
// List
// ============================================================================
/** Row shape in `GET /connectors`. Super_admin-only fields are optional. */
export const ConnectorSummarySchema = z.object({
    id: ConnectorId,
    name: z.string(),
    type: ConnectorType,
    config: ConnectorConfigBlobSchema.nullable(),
    status: ConnectorStatus,
    lastSyncAt: ISO8601Date.nullable(),
    lastError: z.string().nullable(),
    jobCount: z.number().int().nonnegative(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
    tenantId: TenantId.optional(),
    tenantName: z.string().optional(),
    tenantSlug: TenantSlug.optional(),
});
export const ListConnectorsResponseSchema = z.object({
    connectors: z.array(ConnectorSummarySchema),
    count: z.number().int().nonnegative(),
});
// ============================================================================
// Get
// ============================================================================
/** Recent job run returned alongside `GET /connectors/:id`. */
export const ConnectorRunRefSchema = z.object({
    id: JobRunId,
    jobName: z.string(),
    status: JobRunStatus,
    startedAt: ISO8601Date.nullable(),
    completedAt: ISO8601Date.nullable(),
    rowsLoaded: z.number().int().nonnegative().nullable(),
});
/**
 * Detail shape. `credentialsSecretArn` is rendered as either `'(configured)'`
 * or `null` (never the raw ARN) so the UI can show a presence badge without
 * leaking infrastructure identifiers.
 */
export const ConnectorDetailSchema = z.object({
    id: ConnectorId,
    name: z.string(),
    type: ConnectorType,
    config: ConnectorConfigBlobSchema.nullable(),
    credentialsSecretArn: z.string().nullable(),
    status: ConnectorStatus,
    lastSyncAt: ISO8601Date.nullable(),
    lastError: z.string().nullable(),
    jobCount: z.number().int().nonnegative(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
    createdBy: UserId.nullable(),
});
export const GetConnectorResponseSchema = z.object({
    connector: ConnectorDetailSchema,
    recentRuns: z.array(ConnectorRunRefSchema),
});
// ============================================================================
// Create
// ============================================================================
/** `POST /connectors`. Credentials (if any) are routed to Secrets Manager. */
export const CreateConnectorRequestSchema = z.object({
    name: z.string().min(1).max(100),
    type: ConnectorType,
    config: ConnectorConfigBlobSchema,
    credentials: ConnectorCredentialsSchema.optional(),
});
export const CreateConnectorResponseSchema = z.object({
    message: z.string().min(1),
    connector: z.object({
        id: ConnectorId,
        name: z.string(),
        type: ConnectorType,
        status: ConnectorStatus,
        createdAt: ISO8601Date,
    }),
});
// ============================================================================
// Update
// ============================================================================
/** `PUT /connectors/:id`. All fields optional; handler requires at least one. */
export const UpdateConnectorRequestSchema = z
    .object({
    name: z.string().min(1).max(100),
    config: ConnectorConfigBlobSchema,
    credentials: ConnectorCredentialsSchema,
    status: ConnectorWritableStatus,
})
    .partial();
export const UpdateConnectorResponseSchema = z.object({
    message: z.string().min(1),
    connector: z.object({
        id: ConnectorId,
        name: z.string(),
        type: ConnectorType,
        status: ConnectorStatus,
        updatedAt: ISO8601Date,
    }),
});
// ============================================================================
// Delete
// ============================================================================
export const DeleteConnectorResponseSchema = z.object({
    message: z.string().min(1),
    deletedConnector: z.object({
        id: ConnectorId,
        name: z.string(),
    }),
});
// ============================================================================
// Test
// ============================================================================
/** `POST /connectors/:id/test` — probes external connectivity. */
export const TestConnectorResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).nullable(),
    status: ConnectorStatus,
});
// ============================================================================
// Stats
// ============================================================================
/** Per-connector aggregate over the last 30 days. */
export const ConnectorStatsSchema = z.object({
    totalRuns30d: z.number().int().nonnegative(),
    successfulRuns30d: z.number().int().nonnegative(),
    failedRuns30d: z.number().int().nonnegative(),
    successRate: z.number().min(0).max(100),
    totalRowsLoaded30d: z.number().int().nonnegative(),
    avgDurationMs: z.number().int().nonnegative(),
    lastRunAt: ISO8601Date.nullable(),
});
/** Daily run rollup; used by the 7-day sparkline on connector cards. */
export const ConnectorDailyTrendPointSchema = z.object({
    date: z.string(), // raw `DATE` from MySQL — kept loose for UI flexibility
    runs: z.number().int().nonnegative(),
    success: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    rowsLoaded: z.number().int().nonnegative(),
});
export const GetConnectorStatsResponseSchema = z.object({
    stats: ConnectorStatsSchema,
    dailyTrend: z.array(ConnectorDailyTrendPointSchema),
});
// ============================================================================
// Upload flow — Smart Import wide-format detection
// ============================================================================
/** Per-entity preview returned when Smart Import succeeds (>=0.5 confidence). */
export const SmartImportEntityPreviewSchema = z.object({
    count: z.number().int().nonnegative(),
    sample: z.array(z.unknown()),
});
export const SmartImportYearRangeSchema = z.object({
    min: z.number().int(),
    max: z.number().int(),
});
/** Successful detection: handler emits the full entity preview. */
export const SmartImportDetectedSchema = z.object({
    detected: z.literal(true),
    confidence: z.number().min(0).max(1),
    yearColumns: z.array(z.string()),
    entityColumns: z.record(z.string(), z.string()),
    entities: z.object({
        customers: SmartImportEntityPreviewSchema,
        brands: SmartImportEntityPreviewSchema,
        products: SmartImportEntityPreviewSchema,
    }),
    estimatedFactRows: z.number().int().nonnegative(),
    yearRange: SmartImportYearRangeSchema.nullable(),
});
/** Year columns present but confidence too low for auto-import. */
export const SmartImportLowConfidenceSchema = z.object({
    detected: z.literal(false),
    confidence: z.number().min(0).max(1),
    yearColumns: z.array(z.string()),
    entityColumns: z.record(z.string(), z.string()),
    message: z.string(),
});
export const SmartImportInfoSchema = z.discriminatedUnion('detected', [
    SmartImportDetectedSchema,
    SmartImportLowConfidenceSchema,
]);
/** File preview embedded in the upload response. */
export const UploadPreviewSchema = z.object({
    headers: z.array(z.string()),
    totalRows: z.number().int().nonnegative(),
    sampleRows: z.array(z.record(z.string(), z.unknown())),
    columnTypes: z.record(z.string(), z.unknown()),
});
export const UploadMappingSuggestionsSchema = z.object({
    mappings: z.record(z.string(), z.record(z.string(), z.string())),
    targetSchemas: z.record(z.string(), z.unknown()),
});
/** `POST /connectors/upload` success payload. */
export const UploadFileResponseSchema = z.object({
    uploadId: UploadId,
    filename: z.string(),
    fileSize: z.number().int().nonnegative(),
    preview: UploadPreviewSchema,
    suggestions: UploadMappingSuggestionsSchema,
    smartImport: SmartImportInfoSchema.nullable(),
    meta: z.record(z.string(), z.unknown()),
});
/**
 * `POST /connectors/upload/:id/confirm`. Either `mappings` or `smartImport.enabled`
 * must be provided (enforced by the handler, not the schema).
 */
export const ConfirmUploadSmartImportSchema = z.object({
    enabled: z.boolean(),
    yearColumns: z.array(z.string()).optional(),
    entityColumns: z.record(z.string(), z.string()).optional(),
    skipZeros: z.boolean().optional(),
    valueColumn: z.string().optional(),
});
export const ConfirmUploadRequestSchema = z.object({
    mappings: z.record(z.string(), z.unknown()).optional(),
    smartImport: ConfirmUploadSmartImportSchema.optional(),
});
export const ConfirmUploadResponseSchema = z.object({
    message: z.string().min(1),
    uploadId: UploadId,
    status: UploadStatus,
});
/** `GET /connectors/upload/:id` — polled by the UI during async processing. */
export const GetUploadStatusResponseSchema = z.object({
    uploadId: UploadId,
    filename: z.string(),
    status: UploadStatus,
    rowCount: z.number().int().nonnegative().nullable(),
    processedRowCount: z.number().int().nonnegative(),
    rejectedRowCount: z.number().int().nonnegative(),
    loadedEntities: z.record(z.string(), z.unknown()).nullable(),
    processingStartedAt: ISO8601Date.nullable(),
    processingCompletedAt: ISO8601Date.nullable(),
    durationMs: NonNegativeInt.nullable(),
    errorMessage: z.string().nullable(),
    columnInfo: z.record(z.string(), z.unknown()),
    mappings: z.record(z.string(), z.unknown()).nullable(),
    smartImportConfig: z.record(z.string(), z.unknown()).nullable(),
    createdAt: ISO8601Date.nullable(),
    updatedAt: ISO8601Date.nullable(),
});
//# sourceMappingURL=index.js.map